'use client';

import Link from '@/components/i18n/LocaleLink';
import Image from 'next/image';
import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { HelpLanguageMenu } from '@/app/help/_components/HelpLanguageMenu';
import { useLegalLocale } from '@/components/legal/termly-policy';
import { localizedHref, stripLocalePrefix } from '@/lib/i18n/href';

type Lang = 'en' | 'es' | 'pt' | 'de' | 'fr';

const LANGS: readonly Lang[] = ['en', 'es', 'pt', 'de', 'fr'];

const legalTranslations: Record<Lang, { backToHome: string; copyright: string; language: string }> = {
  en: { backToHome: '← Back to home', copyright: 'All rights reserved.', language: 'Language' },
  es: { backToHome: '← Volver al inicio', copyright: 'Todos los derechos reservados.', language: 'Idioma' },
  pt: { backToHome: '← Voltar ao início', copyright: 'Todos os direitos reservados.', language: 'Idioma' },
  de: { backToHome: '← Zurück zur Startseite', copyright: 'Alle Rechte vorbehalten.', language: 'Sprache' },
  fr: { backToHome: '← Retour à l\'accueil', copyright: 'Tous droits réservés.', language: 'Langue' },
};

export default function LegalLayout({ children }: { children: React.ReactNode }) {
  // El idioma sale del prefijo de la URL (respaldo: cookie → navegador → en),
  // así que el layout cambia en cuanto se elige otro idioma en el selector.
  const lang = useLegalLocale();
  const pathname = usePathname() || '/';
  const hrefByLocale = useMemo(() => {
    const bare = stripLocalePrefix(pathname);
    return Object.fromEntries(LANGS.map((l) => [l, localizedHref(bare, l)])) as Record<Lang, string>;
  }, [pathname]);

  const t = legalTranslations[lang] ?? legalTranslations.en;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 px-4 py-3 sticky top-0 bg-white/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/images/brand/ezdocuai-logo.png"
              alt="EZDocuAI"
              width={36}
              height={36}
              className="h-8 w-8"
            />
            {/* En móvil solo el logo: deja sitio a «volver» y al selector de idioma. */}
            <span className="hidden sm:inline text-lg font-semibold text-gray-900">EZDocuAI</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link href="/" className="text-sm text-purple-600 hover:text-purple-800 transition-colors font-medium whitespace-nowrap">
              {t.backToHome}
            </Link>
            <HelpLanguageMenu currentLocale={lang} hrefByLocale={hrefByLocale} label={t.language} />
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1">
        {children}
      </div>

      {/* Minimal footer */}
      <footer className="border-t border-gray-200 py-6 px-4">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} EZDocuAI, Inc. {t.copyright}</p>
        </div>
      </footer>
    </div>
  );
}
